import { FC, useEffect } from 'react';
import type { NavigationType, unstable_Blocker as Blocker } from 'react-router-dom';
import { trackEvent } from 'helpers/mixpanelEvents';
import type { LeaveCartModalProps } from 'helpers/mixpanelEvents';
import { ModalComponent } from 'components/Modal';
import { COMPACT_MODAL_CONTENT_STYLE } from 'components/Modal/constants';
import theme from 'config/theme';

// Above the header sidebar (Header/styled.ts), which stays open while navigation is blocked.
const OVERLAY_STYLE = { zIndex: 200 };

type Props = {
  blocker: Blocker;
  historyAction: NavigationType | undefined;
  trackingProps: Omit<LeaveCartModalProps, 'destination_path' | 'history_action'>;
  primaryColor: string;
  secondaryColor: string;
};

export const LeaveCartModal: FC<Props> = ({
  blocker,
  historyAction,
  trackingProps,
  primaryColor,
  secondaryColor,
}) => {
  const isBlocked = blocker.state === 'blocked';
  const eventProps: LeaveCartModalProps = {
    ...trackingProps,
    destination_path: blocker.location?.pathname ?? '',
    history_action: historyAction,
  };

  // Fire once per blocked navigation; re-running on prop changes would double-count.
  useEffect(() => {
    if (isBlocked) trackEvent('Shopping Cart Leave Modal Viewed', eventProps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBlocked, blocker.location?.key]);

  if (!isBlocked) return null;

  const handleStay = () => {
    trackEvent('Shopping Cart Leave Modal Stay Clicked', eventProps);
    blocker.reset();
  };

  const handleLeave = () => {
    trackEvent('Shopping Cart Leave Modal Leave Clicked', eventProps);
    blocker.proceed();
  };

  return (
    <ModalComponent
      isOpen
      closeModal={handleStay}
      contentLabel="Leave your cart?"
      style={COMPACT_MODAL_CONTENT_STYLE}
      overlayStyle={OVERLAY_STYLE}
      iconColor={theme.colors.black}
    >
      <div className="leave-cart-modal">
        <span className="h4-text color-inkBlue">Leave your cart?</span>
        <span className="body-text color-inkBlue">
          Your selections will be lost if you leave this page.
        </span>
        <div className="leave-cart-modal-actions">
          <button
            type="button"
            className="button button-brand"
            style={{ backgroundColor: primaryColor, color: secondaryColor }}
            onClick={handleStay}
          >
            Stay
          </button>
          <button
            type="button"
            className="button button-brand button-brand-secondary button-brand-border"
            onClick={handleLeave}
          >
            Leave anyway
          </button>
        </div>
      </div>
    </ModalComponent>
  );
};
